/**
 * elfParser.ts
 * Arduino IDE が生成した main.ino.elf をブラウザ上でパースして ParsedInoData を返す。
 * main.ino.bin / main.ino.map が同時にアップロードされても無視して .elf のみ使用する。
 */
import type { ParsedInoData } from "./inoParser";

const ELF_MAGIC = [0x7f, 0x45, 0x4c, 0x46]; // \x7fELF
const ELF32_HEADER_SIZE = 52;
const ELF32_SECTION_HEADER_SIZE = 40;
const ELF32_SYMBOL_ENTRY_SIZE = 16;
const MAX_SECTION_HEADER_SIZE = 1024;
const MAX_DAYDREAM_TOTAL_SIZE = 16 * 1024 * 1024;
const MAX_SECTION_COUNT = 4096;
const MAX_SYMBOL_COUNT = 65536;
const MAX_COLLECTION_COUNT = 4096;
const MAX_TOTAL_ENTRY_COUNT = 8192;
const MAX_ENTRY_LENGTH = 4 * 1024 * 1024;
const MAX_STRING_LENGTH = 1024 * 1024;
const MAX_SYMBOL_NAME_LENGTH = 4096;
const UINT32_ADDRESS_SPACE = 0x1_0000_0000;
const SHT_SYMTAB = 2;
const SHT_STRTAB = 3;
const SHT_NOBITS = 8;
const SHF_ALLOC = 0x2;

interface SectionHeader {
    type: number;
    flags: number;
    addr: number;
    fileOffset: number;
    size: number;
    link: number;
    entrySize: number;
}

interface ElfSymbol {
    value: number;
    size: number;
}

function isRangeWithin(
    offset: number,
    length: number,
    totalLength: number,
): boolean {
    return (
        Number.isSafeInteger(offset) &&
        Number.isSafeInteger(length) &&
        offset >= 0 &&
        length >= 0 &&
        offset <= totalLength &&
        length <= totalLength - offset
    );
}

export function isElfFile(buffer: ArrayBuffer): boolean {
    if (buffer.byteLength < ELF_MAGIC.length) return false;
    const bytes = new Uint8Array(buffer, 0, ELF_MAGIC.length);
    return ELF_MAGIC.every((value, index) => bytes[index] === value);
}

export function parseElfFile(buffer: ArrayBuffer): ParsedInoData {
    if (buffer.byteLength > MAX_DAYDREAM_TOTAL_SIZE) {
        throw new Error(
            `Invalid ELF: total size exceeds the Daydream limit (${MAX_DAYDREAM_TOTAL_SIZE} bytes)`,
        );
    }
    if (buffer.byteLength < ELF32_HEADER_SIZE) {
        throw new Error(
            `Invalid ELF header: expected at least ${ELF32_HEADER_SIZE} bytes (got ${buffer.byteLength})`,
        );
    }
    if (!isElfFile(buffer)) throw new Error("Not an ELF file");

    const buf = new Uint8Array(buffer);
    const view = new DataView(buffer);
    if (buf[4] !== 1) throw new Error("Only 32-bit ELF supported");
    if (buf[5] !== 1) throw new Error("Only little-endian ELF supported");

    const headerSize = view.getUint16(40, true);
    const sectionOffset = view.getUint32(32, true);
    const sectionEntrySize = view.getUint16(46, true);
    const sectionCount = view.getUint16(48, true);
    const sectionNameIndex = view.getUint16(50, true);

    if (headerSize !== ELF32_HEADER_SIZE) {
        throw new Error(
            `Invalid ELF header size: expected ${ELF32_HEADER_SIZE}, got ${headerSize}`,
        );
    }
    if (sectionCount === 0 || sectionCount > MAX_SECTION_COUNT) {
        throw new Error(
            `Invalid ELF section count: ${sectionCount} is outside the Daydream limit (1-${MAX_SECTION_COUNT})`,
        );
    }
    if (
        sectionEntrySize < ELF32_SECTION_HEADER_SIZE ||
        sectionEntrySize > MAX_SECTION_HEADER_SIZE
    ) {
        throw new Error(
            `Invalid ELF section entry size: ${sectionEntrySize} is outside ${ELF32_SECTION_HEADER_SIZE}-${MAX_SECTION_HEADER_SIZE} bytes`,
        );
    }

    const sectionTableLength = sectionCount * sectionEntrySize;
    if (!isRangeWithin(sectionOffset, sectionTableLength, buf.length)) {
        throw new Error(
            `Invalid ELF section table: offset ${sectionOffset} and length ${sectionTableLength} extend past ${buf.length} bytes`,
        );
    }
    if (sectionNameIndex >= sectionCount) {
        throw new Error(
            `Invalid ELF section-name string-table index: ${sectionNameIndex}`,
        );
    }

    const shdrs: SectionHeader[] = [];
    for (let i = 0; i < sectionCount; i++) {
        const base = sectionOffset + i * sectionEntrySize;
        const section: SectionHeader = {
            type: view.getUint32(base + 4, true),
            flags: view.getUint32(base + 8, true),
            addr: view.getUint32(base + 12, true),
            fileOffset: view.getUint32(base + 16, true),
            size: view.getUint32(base + 20, true),
            link: view.getUint32(base + 24, true),
            entrySize: view.getUint32(base + 36, true),
        };

        if (section.size > MAX_DAYDREAM_TOTAL_SIZE) {
            throw new Error(
                `Invalid ELF section ${i} size: ${section.size} exceeds the Daydream limit`,
            );
        }
        if (section.size > UINT32_ADDRESS_SPACE - section.addr) {
            throw new Error(
                `Invalid ELF section ${i} address range: 0x${section.addr.toString(16)} + ${section.size} overflows 32 bits`,
            );
        }
        if (
            section.type !== SHT_NOBITS &&
            !isRangeWithin(section.fileOffset, section.size, buf.length)
        ) {
            throw new Error(
                `Invalid ELF section ${i} data range: offset ${section.fileOffset} and size ${section.size} extend past the input`,
            );
        }
        shdrs.push(section);
    }

    if (sectionNameIndex !== 0 && shdrs[sectionNameIndex].type !== SHT_STRTAB) {
        throw new Error(
            `Invalid ELF section-name string table at index ${sectionNameIndex}`,
        );
    }

    function locateDataAtAddr(
        addr: number,
        length: number,
        label: string,
    ): { fileOffset: number; section: SectionHeader } {
        if (
            !Number.isSafeInteger(addr) ||
            addr < 0 ||
            addr >= UINT32_ADDRESS_SPACE
        ) {
            throw new Error(`Invalid ELF ${label} address: ${addr}`);
        }
        if (
            !Number.isSafeInteger(length) ||
            length < 0 ||
            length > MAX_ENTRY_LENGTH
        ) {
            throw new Error(
                `Invalid ELF ${label} entry length: ${length} exceeds the Daydream limit`,
            );
        }

        for (const section of shdrs) {
            if (
                section.size === 0 ||
                section.type === SHT_NOBITS ||
                (section.flags & SHF_ALLOC) === 0 ||
                addr < section.addr
            ) {
                continue;
            }
            const relativeOffset = addr - section.addr;
            if (
                relativeOffset <= section.size &&
                length <= section.size - relativeOffset
            ) {
                return {
                    fileOffset: section.fileOffset + relativeOffset,
                    section,
                };
            }
        }
        throw new Error(
            `Invalid ELF ${label} address range: 0x${addr.toString(16)} + ${length} is not in a file-backed section`,
        );
    }

    let decodedPayloadSize = 0;
    function reservePayload(length: number, label: string): void {
        if (length > MAX_ENTRY_LENGTH) {
            throw new Error(
                `Invalid ELF ${label} entry length: ${length} exceeds the Daydream limit (${MAX_ENTRY_LENGTH} bytes)`,
            );
        }
        if (length > MAX_DAYDREAM_TOTAL_SIZE - decodedPayloadSize) {
            throw new Error(
                `Invalid ELF decoded total size: ${label} exceeds the Daydream limit (${MAX_DAYDREAM_TOTAL_SIZE} bytes)`,
            );
        }
        decodedPayloadSize += length;
    }

    const dec = new TextDecoder("utf-8");
    function readStringAtAddr(addr: number, label: string): string {
        const { fileOffset, section } = locateDataAtAddr(addr, 1, label);
        const relativeOffset = addr - section.addr;
        const availableLength = section.size - relativeOffset;
        const scanLength = Math.min(availableLength, MAX_STRING_LENGTH + 1);
        const scanEnd = fileOffset + scanLength;
        let end = fileOffset;
        while (end < scanEnd && buf[end] !== 0) end++;

        if (end === scanEnd) {
            if (availableLength > MAX_STRING_LENGTH) {
                throw new Error(
                    `Invalid ELF ${label} string length: exceeds the Daydream limit (${MAX_STRING_LENGTH} bytes)`,
                );
            }
            throw new Error(`Invalid ELF ${label}: unterminated string`);
        }

        const length = end - fileOffset;
        reservePayload(length, label);
        return dec.decode(buf.subarray(fileOffset, end));
    }

    const symtabHdr = shdrs.find(section => section.type === SHT_SYMTAB);
    if (!symtabHdr) throw new Error("No symbol table found in ELF");
    if (symtabHdr.link >= shdrs.length) {
        throw new Error(
            `Invalid ELF symbol string-table index: ${symtabHdr.link}`,
        );
    }
    const strtabHdr = shdrs[symtabHdr.link];
    if (strtabHdr.type !== SHT_STRTAB || strtabHdr.size === 0) {
        throw new Error(
            `Invalid ELF symbol string table at index ${symtabHdr.link}`,
        );
    }
    if (
        symtabHdr.entrySize < ELF32_SYMBOL_ENTRY_SIZE ||
        symtabHdr.entrySize > MAX_SECTION_HEADER_SIZE
    ) {
        throw new Error(
            `Invalid ELF symbol entry size: ${symtabHdr.entrySize}`,
        );
    }
    if (symtabHdr.size % symtabHdr.entrySize !== 0) {
        throw new Error(
            `Invalid ELF symbol table size: ${symtabHdr.size} is not a multiple of entry size ${symtabHdr.entrySize}`,
        );
    }

    const symCount = symtabHdr.size / symtabHdr.entrySize;
    if (symCount > MAX_SYMBOL_COUNT) {
        throw new Error(
            `Invalid ELF symbol count: ${symCount} exceeds the Daydream limit (${MAX_SYMBOL_COUNT})`,
        );
    }

    const symbols = new Map<string, ElfSymbol>();
    const symbolNames = new Map<number, string>();
    let decodedSymbolNameSize = 0;
    for (let i = 0; i < symCount; i++) {
        const base = symtabHdr.fileOffset + i * symtabHdr.entrySize;
        const nameOffset = view.getUint32(base, true);
        const value = view.getUint32(base + 4, true);
        const size = view.getUint32(base + 8, true);

        if (nameOffset >= strtabHdr.size) {
            throw new Error(
                `Invalid ELF symbol ${i} name offset: ${nameOffset} is outside the string table`,
            );
        }

        let name = symbolNames.get(nameOffset);
        if (name === undefined) {
            const nameStart = strtabHdr.fileOffset + nameOffset;
            const availableLength = strtabHdr.size - nameOffset;
            const scanLength = Math.min(
                availableLength,
                MAX_SYMBOL_NAME_LENGTH + 1,
            );
            const scanEnd = nameStart + scanLength;
            let nameEnd = nameStart;
            while (nameEnd < scanEnd && buf[nameEnd] !== 0) nameEnd++;

            if (nameEnd === scanEnd) {
                if (availableLength > MAX_SYMBOL_NAME_LENGTH) {
                    throw new Error(
                        `Invalid ELF symbol ${i} name length: exceeds ${MAX_SYMBOL_NAME_LENGTH} bytes`,
                    );
                }
                throw new Error(`Invalid ELF symbol ${i}: unterminated name`);
            }

            const nameLength = nameEnd - nameStart;
            if (nameLength > MAX_DAYDREAM_TOTAL_SIZE - decodedSymbolNameSize) {
                throw new Error(
                    `Invalid ELF decoded symbol-name total size: exceeds the Daydream limit (${MAX_DAYDREAM_TOTAL_SIZE} bytes)`,
                );
            }
            decodedSymbolNameSize += nameLength;
            name = dec.decode(buf.subarray(nameStart, nameEnd));
            symbolNames.set(nameOffset, name);
        }
        if (name) symbols.set(name, { value, size });
    }

    function readUInt32(addr: number, label: string): number {
        const { fileOffset } = locateDataAtAddr(addr, 4, label);
        return view.getUint32(fileOffset, true);
    }

    function readInt32(addr: number, label: string): number {
        const { fileOffset } = locateDataAtAddr(addr, 4, label);
        return view.getInt32(fileOffset, true);
    }

    function readCollectionCount(symbolName: string, label: string): number {
        const symbol = symbols.get(symbolName);
        if (!symbol) return 0;
        if (symbol.size !== 0 && symbol.size < 4) {
            throw new Error(
                `Invalid ELF ${label} count symbol size: ${symbol.size}`,
            );
        }
        const count = readInt32(symbol.value, `${label} count`);
        if (count < 0 || count > MAX_COLLECTION_COUNT) {
            throw new Error(
                `Invalid ELF ${label} count: ${count} is outside the Daydream limit (0-${MAX_COLLECTION_COUNT})`,
            );
        }
        return count;
    }

    const tileCount = readCollectionCount("tile_datadump_size", "tile");
    const mapCount = readCollectionCount("map_datadump_size", "map");
    const musicCount = readCollectionCount("music_datadump_size", "music");

    const jpDumpSym = symbols.get("_ZL11textdump_jp");
    const enDumpSym = symbols.get("_ZL11textdump_en");

    function getTextCount(
        symbol: ElfSymbol | undefined,
        label: string,
    ): number {
        if (!symbol) return 0;
        if (symbol.size % 4 !== 0) {
            throw new Error(
                `Invalid ELF ${label} pointer table size: ${symbol.size}`,
            );
        }
        const count = symbol.size / 4;
        if (count > MAX_COLLECTION_COUNT) {
            throw new Error(
                `Invalid ELF ${label} count: ${count} exceeds the Daydream limit (${MAX_COLLECTION_COUNT})`,
            );
        }
        if (symbol.size > 0) {
            locateDataAtAddr(
                symbol.value,
                symbol.size,
                `${label} pointer table`,
            );
        }
        return count;
    }

    const jpCount = getTextCount(jpDumpSym, "Japanese text");
    const enCount = getTextCount(enDumpSym, "English text");
    const totalEntryCount =
        jpCount + enCount + tileCount + mapCount + musicCount;
    if (totalEntryCount > MAX_TOTAL_ENTRY_COUNT) {
        throw new Error(
            `Invalid ELF total entry count: ${totalEntryCount} exceeds the Daydream limit (${MAX_TOTAL_ENTRY_COUNT})`,
        );
    }

    function readTextArray(
        symbol: ElfSymbol,
        count: number,
        label: string,
    ): string[] {
        const texts: string[] = [];
        for (let i = 0; i < count; i++) {
            const stringAddress = readUInt32(
                symbol.value + i * 4,
                `${label} pointer ${i}`,
            );
            texts.push(
                stringAddress === 0
                    ? ""
                    : readStringAtAddr(stringAddress, `${label} ${i}`),
            );
        }
        return texts;
    }

    const textsJp = jpDumpSym
        ? readTextArray(jpDumpSym, jpCount, "Japanese text")
        : [];
    const textsEn = enDumpSym
        ? readTextArray(enDumpSym, enCount, "English text")
        : [];

    function readBinaryArray(prefix: string, count: number): Uint8Array[] {
        const arrays: Uint8Array[] = [];
        for (let i = 0; i < count; i++) {
            const symbol = symbols.get(`${prefix}${i}`);
            if (!symbol || symbol.size === 0) {
                arrays.push(new Uint8Array(0));
                continue;
            }
            if (symbol.size > MAX_ENTRY_LENGTH) {
                throw new Error(
                    `Invalid ELF ${prefix}${i} entry length: ${symbol.size} exceeds the Daydream limit`,
                );
            }
            const { fileOffset } = locateDataAtAddr(
                symbol.value,
                symbol.size,
                `${prefix}${i}`,
            );
            reservePayload(symbol.size, `${prefix}${i}`);
            arrays.push(buf.slice(fileOffset, fileOffset + symbol.size));
        }
        return arrays;
    }

    const tiles = readBinaryArray("td", tileCount);
    const maps = readBinaryArray("md", mapCount);
    const music = readBinaryArray("sd", musicCount);

    function readApiString(symbolName: string, label: string): string | null {
        const symbol = symbols.get(symbolName);
        if (!symbol) return null;
        if (symbol.size !== 0 && symbol.size < 4) {
            throw new Error(
                `Invalid ELF ${label} pointer size: ${symbol.size}`,
            );
        }
        const stringAddress = readUInt32(symbol.value, `${label} pointer`);
        return stringAddress === 0
            ? null
            : readStringAtAddr(stringAddress, label);
    }

    const apiUrl = readApiString("apiUrl", "API URL");
    const apiToken = readApiString("apiToken", "API token");

    const maxLen = Math.max(textsJp.length, textsEn.length);
    const texts: string[] = [];
    for (let i = 0; i < maxLen; i++) {
        texts.push(textsJp[i] || textsEn[i] || "");
    }

    return {
        texts,
        textsJp,
        textsEn,
        apiUrl,
        apiToken,
        filesDetected: ["main.ino.elf"],
        tiles,
        maps,
        music,
    };
}
